import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import NumberInput from '@/components/ui/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNombresSugeridos } from '@/hooks/useNombresSugeridos';
import type {
  AdicionalCalculo,
  BaseCalculo,
  BasesCalculo,
  CostoFormData,
  ModoCalculo,
  ParteItem,
  TipoBonificacion,
  TipoMoneda,
  TipoItem,
} from '@/types/costo';
import type { DestinoPagoOpcion } from '@/presupuestos/types';
import { CamposCalculo } from './CamposCalculo';
import { SelectorDestinoPago } from './SelectorDestinoPago';
import { armarBonificacion, armarCalculoPorcentual, BASES_CALCULO_VACIAS } from './calculo';

interface ModalCostoProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: CostoFormData) => Promise<void>;
  /**
   * Parte a la que pertenece el item. Viene determinada por la caja desde la que
   * se abrió el modal: acá no hay selector de adquiriente / transferente.
   */
  parte: ParteItem;
  /** Título del modal, para dejar claro en qué caja se está agregando. */
  titulo?: string;
  isLoading?: boolean;
  isSplitView?: boolean;
  splitContainer?: HTMLElement | null;
  /**
   * Permite definir el ítem como porcentaje de la base imponible más una suma
   * fija. Solo para presupuestos proforma; las escrituras no tienen base imponible.
   */
  permitirPorcentaje?: boolean;
  /** Bases del presupuesto sobre las que se calcula un ítem porcentual. */
  bases?: BasesCalculo;
  /** Cotización del dólar: convierte la base si está en otra moneda que el ítem. */
  cotizacionDolar?: number | null;
  /** Destinos de pago para elegir; el ítem arranca con el destino por defecto. */
  destinosPago?: DestinoPagoOpcion[];
}

export const ModalCosto: React.FC<ModalCostoProps> = ({
  isOpen,
  onClose,
  onConfirm,
  parte,
  titulo = 'Agregar Item',
  isLoading = false,
  isSplitView = false,
  splitContainer = null,
  permitirPorcentaje = false,
  bases = BASES_CALCULO_VACIAS,
  cotizacionDolar = null,
  destinosPago,
}) => {
  const [nombre, setNombre] = useState('');
  const [destinoPagoId, setDestinoPagoId] = useState<string | null>(null);
  const [monto, setMonto] = useState('');
  const [modoCalculo, setModoCalculo] = useState<ModoCalculo>('fijo');
  const [baseCalculo, setBaseCalculo] = useState<BaseCalculo>('base_imponible');
  const [porcentaje, setPorcentaje] = useState('');
  const [adicional, setAdicional] = useState<AdicionalCalculo>('suma_fija');
  const [sumaFija, setSumaFija] = useState('');
  // null = en la moneda del ítem, hasta que se elija otra.
  const [monedaSumaFija, setMonedaSumaFija] = useState<TipoMoneda | null>(null);
  const [porcentajeAdicional, setPorcentajeAdicional] = useState('');
  const [moneda, setMoneda] = useState<TipoMoneda>('ARS');
  const [tipo, setTipo] = useState<TipoItem>('egreso');
  const [importeReal, setImporteReal] = useState('');
  const [tipoBonificacion, setTipoBonificacion] = useState<TipoBonificacion>('porcentaje');
  const [bonificacion, setBonificacion] = useState('');
  const [error, setError] = useState('');

  const { opciones: nombresSugeridos, isLoading: isBuscandoNombres } = useNombresSugeridos(nombre);
  const [splitBounds, setSplitBounds] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (!isSplitView || !isOpen || !splitContainer) {
      setSplitBounds(null);
      return;
    }

    const updateBounds = () => {
      const rect = splitContainer.getBoundingClientRect();
      setSplitBounds({ left: rect.left, width: rect.width });
    };

    updateBounds();

    const resizeObserver = new ResizeObserver(updateBounds);
    resizeObserver.observe(splitContainer);
    window.addEventListener('resize', updateBounds);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateBounds);
    };
  }, [isSplitView, isOpen, splitContainer]);

  useEffect(() => {
    if (!isSplitView || !isOpen || !splitContainer) return;

    const previousOverflow = splitContainer.style.overflow;
    splitContainer.style.overflow = 'hidden';

    return () => {
      splitContainer.style.overflow = previousOverflow;
    };
  }, [isSplitView, isOpen, splitContainer]);

  const esPorcentaje = permitirPorcentaje && modoCalculo === 'porcentaje';

  const reiniciarCalculo = () => {
    setModoCalculo('fijo');
    setBaseCalculo('base_imponible');
    setPorcentaje('');
    setAdicional('suma_fija');
    setSumaFija('');
    setMonedaSumaFija(null);
    setPorcentajeAdicional('');
  };

  const destinoPorDefecto = destinosPago?.find((destino) => destino.esDefault)?.id ?? null;

  useEffect(() => {
    reiniciarCalculo();
    setDestinoPagoId(destinoPorDefecto);
    setNombre('');
    setMonto('');
    setMoneda('ARS');
    setTipo('egreso');
    setImporteReal('');
    setTipoBonificacion('porcentaje');
    setBonificacion('');
    setError('');
  }, [isOpen, destinoPorDefecto]);

  const handleConfirm = async () => {
    setError('');

    if (!nombre.trim()) {
      setError('El nombre del item es obligatorio');
      return;
    }

    // Un item puede quedar en cero y completarse después.
    const montoNumerico = !monto || monto.trim() === '' ? 0 : parseFloat(monto);
    if (isNaN(montoNumerico) || montoNumerico < 0) {
      setError('El monto no puede ser negativo');
      return;
    }

    const calculo: Partial<CostoFormData> = {};

    if (esPorcentaje) {
      const porcentual = armarCalculoPorcentual({
        porcentaje,
        adicional,
        sumaFija,
        monedaSumaFija: monedaSumaFija ?? moneda,
        porcentajeAdicional,
      });
      if ('error' in porcentual) {
        setError(porcentual.error);
        return;
      }

      Object.assign(calculo, porcentual.datos, { base_calculo: baseCalculo });
    }

    // El importe real solo aplica a egresos; vacío = el monto es el costo real.
    if (tipo === 'egreso' && importeReal.trim() !== '') {
      const importeRealNumerico = parseFloat(importeReal);
      if (isNaN(importeRealNumerico) || importeRealNumerico < 0) {
        setError('El importe real no puede ser negativo');
        return;
      }
      calculo.importe_real = importeRealNumerico;
    }

    // Bonificación opcional; vacía = sin bonificación.
    if (bonificacion.trim() !== '') {
      const resultado = armarBonificacion(tipoBonificacion, parseFloat(bonificacion.replace(',', '.')));
      if ('error' in resultado) {
        setError(resultado.error);
        return;
      }
      Object.assign(calculo, resultado.datos);
    }

    try {
      await onConfirm({
        nombre: nombre.trim(),
        parte,
        // En modo porcentaje el monto lo calcula el backend.
        monto: esPorcentaje ? 0 : montoNumerico,
        moneda,
        tipo,
        ...calculo,
        // Sin selector, el backend asigna el destino por defecto.
        ...(destinosPago ? { destino_pago_id: destinoPagoId } : {}),
      });
      handleClose();
    } catch (err) {
      setError('Error al guardar el item. Intente nuevamente.');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reiniciarCalculo();
      setNombre('');
      setMonto('');
      setMoneda('ARS');
      setTipo('egreso');
      setImporteReal('');
      setTipoBonificacion('porcentaje');
      setBonificacion('');
      setError('');
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      handleClose();
    }
  };

  const splitViewStyle = splitBounds
    ? {
        position: 'fixed' as const,
        top: window.innerHeight / 2,
        left: splitBounds.left + splitBounds.width / 2,
        width: `min(calc(${splitBounds.width}px - 3rem), 48rem)`,
        maxWidth: `calc(${splitBounds.width}px - 3rem)`,
        maxHeight: 'calc(100dvh - 3rem)',
      }
    : undefined;

  const splitOverlayStyle = splitBounds
    ? {
        position: 'fixed' as const,
        top: 0,
        left: splitBounds.left,
        width: splitBounds.width,
        height: '100dvh',
      }
    : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={!isSplitView}>
      <DialogContent
        keepOpenOnOutsideInteract={isSplitView}
        forceOverlay={isSplitView}
        className={isSplitView ? 'overflow-y-auto shadow-2xl' : undefined}
        style={isSplitView ? splitViewStyle : undefined}
        overlayStyle={isSplitView ? splitOverlayStyle : undefined}
      >
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="nombre-item">Nombre *</Label>
            <AutocompleteInput
              value={nombre}
              onChange={(v) => {
                setNombre(v);
                setError('');
              }}
              onSelect={(opt) => {
                if (opt) setNombre(opt.nombre);
              }}
              options={nombresSugeridos}
              getOptionLabel={(o) => o.nombre}
              placeholder="Ej: Honorarios, Sellados, Seña, etc."
              isLoading={isBuscandoNombres}
              emptyMessage="No hay sugerencias. Escribí el nombre del item."
              disabled={isLoading}
            />
          </div>

          {destinosPago && (
            <div className="space-y-2">
              <Label>Destino de pago</Label>
              <div>
                <SelectorDestinoPago
                  value={destinoPagoId}
                  opciones={destinosPago}
                  onChange={setDestinoPagoId}
                  ariaLabel="Destino de pago"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Moneda */}
          <div className="space-y-2">
            <Label>Moneda *</Label>
            <div className="flex gap-6">
              {(['ARS', 'USD'] as TipoMoneda[]).map((m) => (
                <label
                  key={m}
                  className={`flex items-center gap-2 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="moneda"
                    value={m}
                    checked={moneda === m}
                    onChange={() => {
                      setMoneda(m);
                      setError('');
                    }}
                    disabled={isLoading}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                    moneda === m ? 'border-primary' : 'border-muted-foreground/40'
                  }`}>
                    {moneda === m && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className={`text-sm font-medium transition-colors ${moneda === m ? 'text-primary' : 'text-foreground'}`}>
                    {m}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {permitirPorcentaje && (
            <CamposCalculo
              idPrefix="nuevo-item"
              modo={modoCalculo}
              onModoChange={(modo) => {
                setModoCalculo(modo);
                setError('');
              }}
              porcentaje={porcentaje}
              onPorcentajeChange={(valor) => {
                setPorcentaje(valor);
                setError('');
              }}
              adicional={adicional}
              onAdicionalChange={(valor) => {
                setAdicional(valor);
                setError('');
              }}
              sumaFija={sumaFija}
              onSumaFijaChange={(valor) => {
                setSumaFija(valor);
                setError('');
              }}
              monedaSumaFija={monedaSumaFija ?? moneda}
              onMonedaSumaFijaChange={(valor) => {
                setMonedaSumaFija(valor);
                setError('');
              }}
              porcentajeAdicional={porcentajeAdicional}
              onPorcentajeAdicionalChange={(valor) => {
                setPorcentajeAdicional(valor);
                setError('');
              }}
              baseCalculo={baseCalculo}
              onBaseCalculoChange={setBaseCalculo}
              moneda={moneda}
              cotizacionDolar={cotizacionDolar}
              bases={bases}
              disabled={isLoading}
            />
          )}

          {/* Monto */}
          {!esPorcentaje && (
            <div className="space-y-2">
              <Label htmlFor="monto-item">Importe</Label>
              <NumberInput
                id="monto-item"
                currency={true}
                decimals={2}
                min={0}
                value={monto}
                onChange={(value) => {
                  setMonto(value);
                  setError('');
                }}
                placeholder="0.00"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Bonificación: porcentaje o monto fijo que se descuenta del importe */}
          <div className="space-y-2">
            <Label htmlFor="bonificacion-item">Bonificación</Label>
            <div className="flex gap-2">
              <Select
                value={tipoBonificacion}
                onValueChange={(valor: TipoBonificacion) => {
                  setTipoBonificacion(valor);
                  setError('');
                }}
                disabled={isLoading}
              >
                <SelectTrigger className="w-20" aria-label="Tipo de bonificación">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="porcentaje">%</SelectItem>
                  <SelectItem value="monto">{!esPorcentaje && moneda === 'USD' ? 'US$' : '$'}</SelectItem>
                </SelectContent>
              </Select>
              <NumberInput
                id="bonificacion-item"
                decimals={tipoBonificacion === 'porcentaje' ? 4 : 2}
                min={0}
                max={tipoBonificacion === 'porcentaje' ? 100 : undefined}
                value={bonificacion}
                onChange={(value) => {
                  setBonificacion(value);
                  setError('');
                }}
                placeholder="Sin bonificación"
                disabled={isLoading}
                containerClassName="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Al cliente se le cobra el importe bonificado. En un egreso, la escribanía
              sigue pagando el importe completo.
            </p>
          </div>

          {/* Tipo */}
          <div className="space-y-2">
            <Label>Tipo *</Label>
            <div className="flex gap-6">
              {([
                { value: 'egreso', label: 'Egreso', color: 'red' },
                { value: 'ingreso', label: 'Ingreso', color: 'green' },
              ] as { value: TipoItem; label: string; color: string }[]).map(
                ({ value, label, color }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="tipo"
                      value={value}
                      checked={tipo === value}
                      onChange={() => {
                        setTipo(value);
                        setError('');
                      }}
                      disabled={isLoading}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                      tipo === value
                        ? color === 'red' ? 'border-red-500' : 'border-green-500'
                        : 'border-muted-foreground/40'
                    }`}>
                      {tipo === value && (
                        <div className={`w-2 h-2 rounded-full ${color === 'red' ? 'bg-red-500' : 'bg-green-500'}`} />
                      )}
                    </div>
                    <span className={`text-sm font-medium transition-colors ${
                      tipo === value
                        ? color === 'red' ? 'text-red-600' : 'text-green-600'
                        : 'text-foreground'
                    }`}>
                      {label}
                    </span>
                  </label>
                )
              )}
            </div>
          </div>

          {/* Importe real: costo efectivo para la escribanía, solo egresos */}
          {tipo === 'egreso' && (
            <div className="space-y-2">
              <Label htmlFor="importe-real-item">Importe real</Label>
              <NumberInput
                id="importe-real-item"
                currency={true}
                decimals={2}
                min={0}
                value={importeReal}
                onChange={(value) => {
                  setImporteReal(value);
                  setError('');
                }}
                placeholder="Igual al importe"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Lo que realmente le cuesta a la escribanía. Contabilidad lo usa como
                egreso; si queda vacío se toma el importe. No figura en el PDF.
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 border border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isLoading}>
            Agregar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
